import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../../contexts/ThemeContext';
import { Plus, Edit, Trash2, Tag, DollarSign, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;

const CORES_DISPONIVEIS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#06B6D4', '#6366F1', '#14B8A6', '#F97316'
];

const CategoriasView = ({ onVoltar }) => {
  const { theme: themeColors, isDarkMode } = useTheme();
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [tipoFiltro, setTipoFiltro] = useState('');
  
  const [formData, setFormData] = useState({
    nome: '',
    tipo: 'despesa',
    cor: '#3B82F6',
    icone: ''
  });

  useEffect(() => {
    loadCategorias();
  }, []);

  // Fechar modal ao pressionar ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategoria(null);
    resetForm();
  };

  const loadCategorias = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/financeiro/categorias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategorias(response.data.categorias || []);
    } catch (error) {
      console.error('Error loading categorias:', error);
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingCategoria) {
        await axios.put(`${API}/financeiro/categorias/${editingCategoria.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Categoria atualizada!');
      } else {
        await axios.post(`${API}/financeiro/categorias`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Categoria criada!');
      }
      
      setShowModal(false);
      setEditingCategoria(null);
      resetForm();
      loadCategorias();
    } catch (error) {
      console.error('Error saving categoria:', error);
      toast.error('Erro ao salvar categoria');
    }
  };

  const handleDelete = async (categoria) => {
    if (!window.confirm(`Excluir "${categoria.nome}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/financeiro/categorias/${categoria.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Categoria excluída!');
      loadCategorias();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Erro ao excluir categoria');
    }
  };

  const openEditModal = (categoria) => {
    setEditingCategoria(categoria);
    setFormData({
      nome: categoria.nome,
      tipo: categoria.tipo,
      cor: categoria.cor || '#3B82F6',
      icone: categoria.icone || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: 'despesa',
      cor: '#3B82F6',
      icone: ''
    });
  };

  const categoriasFiltradas = tipoFiltro 
    ? categorias.filter(c => c.tipo === tipoFiltro)
    : categorias;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <button
          onClick={onVoltar}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'transparent',
            border: `1px solid ${themeColors.border}`,
            borderRadius: '8px',
            color: themeColors.text,
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          <ArrowLeft size={18} />
          Voltar
        </button>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: themeColors.text, marginBottom: '8px' }}>
          Categorias
        </h1>
        <p style={{ color: themeColors.textSecondary, fontSize: '14px' }}>
          Gerencie categorias personalizadas para receitas e despesas
        </p>
      </div>

      {/* Filters and Add Button */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setTipoFiltro('')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: tipoFiltro === '' ? 'none' : `1px solid ${themeColors.border}`,
              background: tipoFiltro === '' ? 'linear-gradient(135deg, #06B6D4, #3B82F6)' : 'transparent',
              color: tipoFiltro === '' ? 'white' : themeColors.text,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Todas
          </button>
          <button
            onClick={() => setTipoFiltro('receita')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: tipoFiltro === 'receita' ? 'none' : `1px solid ${themeColors.border}`,
              background: tipoFiltro === 'receita' ? '#10B981' : 'transparent',
              color: tipoFiltro === 'receita' ? 'white' : themeColors.text,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Receitas
          </button>
          <button
            onClick={() => setTipoFiltro('despesa')}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: tipoFiltro === 'despesa' ? 'none' : `1px solid ${themeColors.border}`,
              background: tipoFiltro === 'despesa' ? '#EF4444' : 'transparent',
              color: tipoFiltro === 'despesa' ? 'white' : themeColors.text,
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Despesas
          </button>
        </div>

        <button
          onClick={() => {
            setEditingCategoria(null);
            resetForm();
            setShowModal(true);
          }}
          style={{
            padding: '10px 24px',
            background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)'
          }}
        >
          <Plus size={18} />
          Nova Categoria
        </button>
      </div>

      {/* Categories List - Table Style */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <p style={{ color: themeColors.textSecondary }}>Carregando...</p>
        </div>
      ) : categoriasFiltradas.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          background: isDarkMode ? '#252b3b' : themeColors.bgCard,
          border: `1px solid ${themeColors.border}`,
          borderRadius: '12px'
        }}>
          <Tag size={48} style={{ color: themeColors.textSecondary, marginBottom: '16px' }} />
          <p style={{ fontSize: '16px', color: themeColors.textSecondary, marginBottom: '8px' }}>
            Nenhuma categoria encontrada
          </p>
          <p style={{ fontSize: '14px', color: themeColors.textSecondary }}>
            Crie sua primeira categoria clicando no botão acima
          </p>
        </div>
      ) : (
        <div style={{
          background: isDarkMode ? '#252b3b' : themeColors.bgCard,
          border: `1px solid ${themeColors.border}`,
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 120px 100px',
            padding: '16px 20px',
            background: isDarkMode ? 'rgba(6, 182, 212, 0.05)' : 'rgba(6, 182, 212, 0.03)',
            borderBottom: `1px solid ${themeColors.border}`,
            fontWeight: '600',
            fontSize: '13px',
            color: themeColors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            <div>Cor</div>
            <div>Nome</div>
            <div>Tipo</div>
            <div style={{ textAlign: 'right' }}>Ações</div>
          </div>

          {/* Table Body */}
          {categoriasFiltradas.map((categoria, index) => (
            <div
              key={categoria.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '60px 1fr 120px 100px',
                padding: '16px 20px',
                borderBottom: index < categoriasFiltradas.length - 1 ? `1px solid ${themeColors.border}` : 'none',
                alignItems: 'center',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? 'rgba(6, 182, 212, 0.05)' : 'rgba(6, 182, 212, 0.03)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {/* Color Circle */}
              <div>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  background: categoria.cor || '#3B82F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {categoria.tipo === 'receita' ? (
                    <DollarSign size={20} style={{ color: 'white' }} />
                  ) : (
                    <Tag size={20} style={{ color: 'white' }} />
                  )}
                </div>
              </div>

              {/* Name */}
              <div>
                <h3 style={{ 
                  fontSize: '15px', 
                  fontWeight: '600', 
                  color: themeColors.text,
                  margin: 0
                }}>
                  {categoria.nome}
                </h3>
              </div>

              {/* Type Badge */}
              <div>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  background: categoria.tipo === 'receita' 
                    ? 'rgba(16, 185, 129, 0.1)'
                    : 'rgba(239, 68, 68, 0.1)',
                  color: categoria.tipo === 'receita' ? '#10B981' : '#EF4444',
                  display: 'inline-block'
                }}>
                  {categoria.tipo === 'receita' ? 'Receita' : 'Despesa'}
                </span>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal(categoria);
                  }}
                  style={{
                    padding: '6px',
                    background: 'transparent',
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: themeColors.text,
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  title="Editar categoria"
                >
                  <Edit size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(categoria);
                  }}
                  style={{
                    padding: '6px',
                    background: 'transparent',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    color: '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                  title="Excluir categoria"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div 
          onMouseDown={(e) => {
            // Se clique começou no backdrop, marcar para fechar
            if (e.target === e.currentTarget) {
              e.currentTarget.dataset.closeOnMouseUp = 'true';
            }
          }}
          onMouseUp={(e) => {
            // Só fechar se mousedown E mouseup foram no backdrop
            if (e.target === e.currentTarget && e.currentTarget.dataset.closeOnMouseUp === 'true') {
              handleCloseModal();
            }
            delete e.currentTarget.dataset.closeOnMouseUp;
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div 
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: isDarkMode ? '#252b3b' : 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '90%',
              maxWidth: '500px'
            }}
          >
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: themeColors.text,
              marginBottom: '24px'
            }}>
              {editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: themeColors.text,
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Ex: Equipamentos, Vendas..."
                  required
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    border: `2px solid ${themeColors.border}`,
                    background: isDarkMode ? '#1a1f2e' : 'white',
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColors.accent;
                    e.target.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeColors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: themeColors.text,
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Tipo *
                </label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo: 'despesa' })}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: formData.tipo === 'despesa' ? 'none' : `2px solid ${themeColors.border}`,
                      background: formData.tipo === 'despesa' ? '#EF4444' : 'transparent',
                      color: formData.tipo === 'despesa' ? 'white' : themeColors.text,
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Despesa
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo: 'receita' })}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '8px',
                      border: formData.tipo === 'receita' ? 'none' : `2px solid ${themeColors.border}`,
                      background: formData.tipo === 'receita' ? '#10B981' : 'transparent',
                      color: formData.tipo === 'receita' ? 'white' : themeColors.text,
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Receita
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: themeColors.text,
                  display: 'block',
                  marginBottom: '8px'
                }}>
                  Cor
                </label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {CORES_DISPONIVEIS.map(cor => (
                    <button
                      key={cor}
                      type="button"
                      onClick={() => setFormData({ ...formData, cor })}
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        background: cor,
                        border: formData.cor === cor ? `2px solid ${themeColors.accent}` : '2px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: formData.cor === cor ? '0 0 0 2px rgba(6, 182, 212, 0.2)' : 'none',
                        transform: formData.cor === cor ? 'scale(1.05)' : 'scale(1)'
                      }}
                      onMouseEnter={(e) => {
                        if (formData.cor !== cor) {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.cor !== cor) {
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    />
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCategoria(null);
                    resetForm();
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'transparent',
                    border: `2px solid ${themeColors.border}`,
                    borderRadius: '10px',
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 32px',
                    background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)'
                  }}
                >
                  {editingCategoria ? 'Salvar Alterações' : 'Criar Categoria'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriasView;
